import { Button, CircularProgress, Stack, Typography } from "@mui/material";
import { useUnblockUserMutation } from "Api/Mutations/Social/useUnblockUserMutation";
import { useBlockedUsersQuery } from "Api/Queries/Social/useBlockedUsersQuery";
import { PrimaryPaper } from "Components/Shared/PrimaryPaper";
import { SocialSummaryRow } from "Components/Social/SocialSummaryRow";
import { SocialListPagination } from "Pages/People/SocialListPagination";
import { useSnapBackToLastPage } from "Pages/People/useSocialListPage";
import { useState } from "react";
import { Resources, useResource } from "Translations/Resources";

const RESOURCES = Resources.Social.People;

/**
 * A page of the personas the caller has blocked, with an unblock action per row.
 * Pages via local state — the section lives outside the tabs, so it must not share
 * the URL page parameter with the mounted tab's list.
 */
export const BlockedListSection = () => {
  const { t } = useResource();
  const [currentPage, setPage] = useState(1);
  const { data: blocked, isLoading } = useBlockedUsersQuery(currentPage);
  useSnapBackToLastPage(blocked, currentPage, setPage);
  const { mutate: unblockUser, isPending } = useUnblockUserMutation();

  return (
    <PrimaryPaper>
      <Stack gap={3}>
        {isLoading && (
          <Stack alignItems="center" py={1}>
            <CircularProgress size={24} />
          </Stack>
        )}
        {blocked?.totalCount === 0 && (
          <Typography variant="body2" color="text.secondary">
            {t(RESOURCES.NoBlocked)}
          </Typography>
        )}
        {!!blocked?.items.length && (
          <Stack gap={4}>
            {blocked.items.map((blockedUser) => (
              <SocialSummaryRow
                key={blockedUser.identityUserPublicID}
                user={blockedUser}
                action={
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    disabled={isPending}
                    onClick={() =>
                      unblockUser(blockedUser.identityUserPublicID)
                    }
                  >
                    {t(RESOURCES.Unblock)}
                  </Button>
                }
              />
            ))}
          </Stack>
        )}
        {blocked && (
          <SocialListPagination
            totalCount={blocked.totalCount}
            currentPage={currentPage}
            onPageChange={setPage}
          />
        )}
      </Stack>
    </PrimaryPaper>
  );
};
